import { useState, useEffect } from "react";
import { useNavigate, useLocation, Outlet, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { 
  Building2, Store, Truck, DollarSign, ClipboardList, 
  AlertTriangle, MessageSquare, LogOut, Menu, X, Home,
  Package
} from "lucide-react";
import { clearSessionToken } from "@/App";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function OpsLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`);
        if (response.data.role !== "operations_manager") {
          navigate("/ops/login");
          return;
        }
        setUser(response.data);
      } catch {
        navigate("/ops/login");
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
      clearSessionToken();
      navigate("/ops/login");
    } catch {
      clearSessionToken();
      navigate("/ops/login");
    }
  };

  const navItems = [
    { path: "/ops/dashboard", icon: Home, label: "Dashboard" },
    { path: "/ops/locations", icon: Building2, label: "My Locations" },
    { path: "/ops/inventory", icon: Package, label: "Inventory" },
    { path: "/ops/dispatches", icon: Truck, label: "Dispatches" },
    { path: "/ops/cash-collection", icon: DollarSign, label: "Cash Collection" },
    { path: "/ops/checklist", icon: ClipboardList, label: "Daily Checklist" },
    { path: "/ops/incidents", icon: AlertTriangle, label: "Incident Reports" },
    { path: "/ops/issues", icon: MessageSquare, label: "Management Issues" },
    { path: "/ops/orders", icon: Store, label: "Orders" },
  ];

  const isActive = (path) => location.pathname === path;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-purple-700 text-white transform transition-transform duration-200 ease-in-out
        ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
        md:translate-x-0 md:static
      `}>
        <div className="p-4 border-b border-purple-600">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-bold text-lg">Ops Portal</h1>
              <p className="text-purple-200 text-sm">{user?.name}</p>
            </div>
            <Button 
              variant="ghost" 
              size="icon" 
              className="md:hidden text-white"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>
        
        <nav className="p-4 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              className={`
                flex items-center gap-3 px-3 py-2 rounded-lg transition-colors
                ${isActive(item.path) 
                  ? "bg-white/20 text-white" 
                  : "text-purple-200 hover:bg-white/10 hover:text-white"
                }
              `}
            >
              <item.icon className="h-5 w-5" />
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-purple-600">
          <Button 
            variant="ghost" 
            className="w-full text-purple-200 hover:text-white hover:bg-white/10 justify-start gap-3"
            onClick={handleLogout}
          >
            <LogOut className="h-5 w-5" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Mobile header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-purple-700 text-white p-4 flex items-center justify-between">
        <Button 
          variant="ghost" 
          size="icon" 
          className="text-white"
          onClick={() => setSidebarOpen(true)}
        >
          <Menu className="h-5 w-5" />
        </Button>
        <h1 className="font-bold">Ops Portal</h1>
        <div className="w-10" /> {/* Spacer */}
      </div>

      {/* Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="flex-1 md:ml-0 pt-16 md:pt-0">
        <div className="p-4 md:p-6 max-w-6xl mx-auto">
          <Outlet context={{ user }} />
        </div>
      </main>
    </div>
  );
}
